import { z } from "zod";

export enum TroubleType {
  ON_HOLD = "ON_HOLD",
  MISMATCH = "MISMATCH",
  ERROR = "ERROR",
  /**
   * A matched order is not yet in a status that allows matching in the source
   * ERP — typically because someone still has to finish entering the order's
   * fields. We don't persist a match against data that may still change; the
   * workflow defers and re-checks later, and the entry clears once the order is
   * ready.
   */
  ORDER_NOT_READY = "ORDER_NOT_READY",
  /**
   * The invoice's references resolved to real source documents, but none of
   * them carry any cost/charge record to match against — e.g. a carrier
   * freight invoice whose load was never given an expected freight cost in
   * the source ERP. Matching cannot succeed until the cost is entered
   * upstream; the entry self-clears on the rerun that finds charges. Set only
   * on confirmed absence (all lookups succeeded), never on lookup failure.
   */
  EXPECTED_COST_MISSING = "EXPECTED_COST_MISSING",
  /**
   * The invoice line items do not add up to the document total (tax counted
   * once). Approval stays blocked until the amounts agree.
   */
  LINE_TOTAL_MISMATCH = "LINE_TOTAL_MISMATCH",
  /**
   * The invoice cites a source-ERP load that carries freight but has no orders
   * on it. Freight on a load with no orders is usually a data-entry error
   * (the same freight is normally on the PO), so the load is kept out of
   * automatic matching and a reviewer decides. The entry names the load and
   * its freight; it self-clears on the rerun that no longer finds one.
   */
  ORDER_LESS_LOAD_FREIGHT = "ORDER_LESS_LOAD_FREIGHT",
}

export const TroubleEntrySchema = z.object({
  active: z.boolean(),
  type: z.string(),
  source: z.string().optional(),
  value: z.string().optional(),
  comments: z.array(z.string()).optional(),
});

export const TroubleDataSchema = z
  .array(TroubleEntrySchema)
  .nullable()
  .optional();

export type TroubleEntry = z.infer<typeof TroubleEntrySchema>;
export type TroubleData = z.infer<typeof TroubleDataSchema>;

/**
 * Pull a `troubleData` array out of a JSON metadata blob (e.g. a Sequelize
 * `metadata` / `originMetadata` column). Returns null when the blob is missing
 * or its `troubleData` key doesn't hold valid trouble entries.
 */
export function extractTroubleData(
  metadata: Record<string, unknown> | null | undefined,
): TroubleData {
  if (!metadata || typeof metadata !== "object") return null;
  const parsed = TroubleDataSchema.safeParse(metadata.troubleData);
  return parsed.success ? (parsed.data ?? null) : null;
}
